import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/App';
import { wallet as walletApi, services as svcApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Wallet, Plus, ArrowUpRight, ArrowDownRight, DollarSign, Loader2, TrendingDown, Clock, AlertTriangle, CreditCard, RefreshCw } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const PRESET_AMOUNTS = [10, 25, 50, 100, 250, 500];

export default function Billing() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txnTotal, setTxnTotal] = useState(0);
  const [customAmount, setCustomAmount] = useState('');
  const [loading, setLoading] = useState('');
  const [allSvc, setAllSvc] = useState({});
  const [autoTopup, setAutoTopup] = useState({ enabled: false, threshold: 10, amount: 50 });

  const loadData = () => {
    walletApi.get().then(r => {
      setWalletData(r.data);
      const w = r.data;
      if (w.auto_topup_enabled !== undefined) {
        setAutoTopup({ enabled: w.auto_topup_enabled || false, threshold: w.auto_topup_threshold || 10, amount: w.auto_topup_amount || 50 });
      }
    }).catch(() => {});
    walletApi.transactions({ limit: 30 }).then(r => { setTransactions(r.data.transactions || []); setTxnTotal(r.data.total || 0); }).catch(() => {});
    svcApi.list().then(r => setAllSvc(r.data.services || {})).catch(() => {});
  };

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    const sid = searchParams.get('session_id');
    if (sid) pollStatus(sid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const pollStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 6) { toast.error('Payment status check timed out'); return; }
    try {
      const res = await walletApi.topupStatus(sessionId);
      if (res.data.payment_status === 'paid') {
        toast.success('Wallet topped up successfully!');
        loadData();
        window.history.replaceState({}, '', '/dashboard/billing');
        return;
      }
      if (res.data.status === 'expired') { toast.error('Session expired'); return; }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch {
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    }
  };

  const handleTopup = async (amount) => {
    const a = parseFloat(amount);
    if (!a || a < 5) { toast.error('Minimum $5.00'); return; }
    setLoading(String(a));
    try {
      const res = await walletApi.topup({ amount: a, origin_url: window.location.origin });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Top-up failed');
    }
    setLoading('');
  };

  const balance = walletData?.balance || 0;
  const dailyData = walletData?.daily_consumption || [];
  const breakdown = walletData?.month_usage?.breakdown || {};
  const monthTotal = walletData?.month_usage?.total || 0;
  const tooltipStyle = { contentStyle: { background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', fontSize: '12px' } };

  return (
    <div data-testid="billing-page" className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Wallet & Billing</h2>
        <p className="text-muted-foreground mt-1">Prepaid usage-based billing. Add credit, use services, pay only for what you consume.</p>
      </div>

      {/* Wallet Balance */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className={`bg-card border-border/50 ${balance <= 0 ? 'border-destructive/50' : balance < 5 ? 'border-amber-500/50' : ''}`}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Wallet Balance</p>
                <p data-testid="wallet-balance" className={`text-4xl font-bold mt-1 font-mono ${balance <= 0 ? 'text-destructive' : balance < 5 ? 'text-amber-400' : 'text-emerald-400'}`}>
                  ${balance.toFixed(2)}
                </p>
                {balance <= 0 && <p className="text-xs text-destructive mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />API calls blocked — add credit</p>}
                {balance > 0 && balance < 5 && <p className="text-xs text-amber-400 mt-1">Low balance warning</p>}
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${balance <= 0 ? 'bg-destructive/15' : 'bg-emerald-500/15'}`}>
                <Wallet className={`w-6 h-6 ${balance <= 0 ? 'text-destructive' : 'text-emerald-400'}`} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="p-5">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">This Month Usage</p>
            <p className="text-3xl font-bold mt-1 font-mono">${monthTotal.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground mt-1">{Object.keys(breakdown).length} services used</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="p-5">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Credited</p>
            <p className="text-3xl font-bold mt-1 font-mono">${(walletData?.total_credited || 0).toFixed(2)}</p>
            <p className="text-xs text-muted-foreground mt-1">Lifetime top-ups</p>
          </CardContent>
        </Card>
      </div>

      {/* Top Up */}
      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Plus className="w-4 h-4 text-primary" />Add Credit</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {PRESET_AMOUNTS.map(a => (
              <Button
                key={a}
                data-testid={`topup-${a}`}
                variant="outline"
                className="h-12 text-base font-mono hover:bg-primary/10 hover:border-primary/40"
                onClick={() => handleTopup(a)}
                disabled={!!loading}
              >
                {loading === String(a) ? <Loader2 className="w-4 h-4 animate-spin" /> : `$${a}`}
              </Button>
            ))}
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input data-testid="topup-custom" type="number" min="5" step="0.01" placeholder="Custom amount (min $5)" value={customAmount} onChange={e => setCustomAmount(e.target.value)} className="pl-8 font-mono" />
            </div>
            <Button data-testid="topup-custom-btn" onClick={() => handleTopup(customAmount)} disabled={!!loading || !customAmount} className="bg-primary hover:bg-primary/90">
              {loading === customAmount ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Credit'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Auto Top-Up */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2"><RefreshCw className="w-4 h-4 text-primary" />Auto Top-Up</CardTitle>
            <Switch data-testid="auto-topup-toggle" checked={autoTopup.enabled} onCheckedChange={async (v) => {
              const updated = { ...autoTopup, enabled: v };
              setAutoTopup(updated);
              try { await walletApi.updateAutoTopup(updated); toast.success(v ? 'Auto top-up enabled' : 'Auto top-up disabled'); } catch (e) { toast.error('Failed to update'); }
            }} />
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">Automatically charge your saved card when balance drops below threshold.</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">When balance drops below ($)</Label>
              <Input data-testid="auto-topup-threshold" type="number" min="1" value={autoTopup.threshold} onChange={e => setAutoTopup(a => ({ ...a, threshold: parseFloat(e.target.value) || 10 }))} onBlur={() => walletApi.updateAutoTopup(autoTopup).catch(() => {})} className="font-mono text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Auto-charge amount ($)</Label>
              <Input data-testid="auto-topup-amount" type="number" min="10" value={autoTopup.amount} onChange={e => setAutoTopup(a => ({ ...a, amount: parseFloat(e.target.value) || 50 }))} onBlur={() => walletApi.updateAutoTopup(autoTopup).catch(() => {})} className="font-mono text-sm" />
            </div>
          </div>
          {walletData?.saved_card_last4 ? (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-secondary/30 border border-border/30">
              <CreditCard className="w-4 h-4 text-primary" />
              <span className="text-sm">{walletData.saved_card_brand || 'Card'} ending in {walletData.saved_card_last4}</span>
              <Badge className="ml-auto bg-emerald-500/20 text-emerald-400 text-[10px]">Saved</Badge>
            </div>
          ) : (
            <Button data-testid="setup-payment" variant="outline" size="sm" onClick={async () => {
              try {
                const res = await walletApi.setupPayment({ origin_url: window.location.origin });
                if (res.data.url) window.location.href = res.data.url;
              } catch (e) { toast.error('Failed to set up payment'); }
            }}>
              <CreditCard className="w-3 h-3 mr-1.5" />Add Payment Method
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Daily Consumption Chart */}
      {dailyData.length > 0 && (
        <Card className="bg-card border-border/50">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><TrendingDown className="w-4 h-4 text-primary" />Daily Consumption (7 days)</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={v => `$${v}`} />
                <Tooltip {...tooltipStyle} formatter={v => [`$${v.toFixed(2)}`, 'Usage']} />
                <Bar dataKey="amount" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Monthly Usage Breakdown */}
      {Object.keys(breakdown).length > 0 && (
        <Card className="bg-card border-border/50">
          <CardHeader><CardTitle className="text-lg">Month-to-Date Breakdown</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(breakdown).map(([key, val]) => (
                <div key={key} className="flex items-center justify-between py-2 border-b border-border/20 last:border-0">
                  <div>
                    <p className="text-sm font-medium">{allSvc[val.service]?.name || val.service}</p>
                    <p className="text-xs text-muted-foreground">{val.tier} tier | {val.hours}h active | {val.requests} requests</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-mono font-bold">${val.cost.toFixed(2)}</p>
                    <p className="text-[10px] text-muted-foreground">${val.rate}/hr</p>
                  </div>
                </div>
              ))}
              <div className="flex justify-between pt-2 font-bold">
                <span>Total</span>
                <span className="font-mono">${monthTotal.toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transaction History */}
      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Clock className="w-4 h-4 text-primary" />Transaction History <Badge variant="outline" className="text-xs ml-1">{txnTotal}</Badge></CardTitle></CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <p className="text-center text-muted-foreground py-8 text-sm">No transactions yet. Add credit to get started.</p>
          ) : (
            <div className="space-y-1">
              {transactions.map((t, i) => (
                <div key={i} data-testid={`txn-${i}`} className="flex items-center justify-between py-2.5 border-b border-border/20 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${t.type === 'credit' ? 'bg-emerald-500/15' : 'bg-amber-500/15'}`}>
                      {t.type === 'credit' ? <ArrowUpRight className="w-3.5 h-3.5 text-emerald-400" /> : <ArrowDownRight className="w-3.5 h-3.5 text-amber-400" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{t.description?.slice(0, 60) || t.type}</p>
                      <p className="text-[10px] text-muted-foreground">{t.created_at ? new Date(t.created_at).toLocaleString() : ''}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-mono font-bold ${t.type === 'credit' ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {t.type === 'credit' ? '+' : '-'}${t.amount?.toFixed(2)}
                    </p>
                    <p className="text-[10px] text-muted-foreground font-mono">Bal: ${t.balance_after?.toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
